/** Helpers para preencher formulários de contrato (carregadores / adequações) a partir do lead. */

export type TipoClienteContrato = 'PF' | 'PJ';

export type CamposClienteContrato = {
  tipoCliente: TipoClienteContrato;
  nome_cliente: string;
  cpf: string;
  cnpj: string;
  representante_legal: string;
  cpf_representante: string;
  endereco: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
};

/** Retorna string trimada se houver conteúdo; senão ''. */
export function textoNaoVazio(value: unknown): string {
  if (value == null) return '';
  const s = String(value).trim();
  return s;
}

/** Preferência: valor do rascunho se preenchido; senão o do lead; senão o atual. */
export function preferirPreenchido(
  rascunho: unknown,
  lead: unknown,
  atual: unknown = ''
): string {
  return textoNaoVazio(rascunho) || textoNaoVazio(lead) || textoNaoVazio(atual);
}

export function tipoClienteFromLead(lead: {
  tipo?: string | null;
} | null | undefined): TipoClienteContrato {
  if (!lead) return 'PF';
  return lead.tipo === 'juridica' || lead.tipo === 'PJ' ? 'PJ' : 'PF';
}

export function camposClienteFromLead(
  lead: Record<string, unknown> | null | undefined
): CamposClienteContrato | null {
  if (!lead) return null;
  const tipoCliente = tipoClienteFromLead(lead as { tipo?: string | null });
  const nomePj = textoNaoVazio(lead.razao_social) || textoNaoVazio(lead.nome);
  const nomePf = textoNaoVazio(lead.nome);
  return {
    tipoCliente,
    nome_cliente: tipoCliente === 'PJ' ? nomePj : nomePf,
    cpf: textoNaoVazio(lead.cpf),
    cnpj: textoNaoVazio(lead.cnpj),
    representante_legal: textoNaoVazio(lead.representante_legal).toUpperCase(),
    cpf_representante: textoNaoVazio(lead.cpf_representante),
    endereco: textoNaoVazio(lead.endereco),
    numero: textoNaoVazio(lead.numero),
    bairro: textoNaoVazio(lead.bairro),
    cidade: textoNaoVazio(lead.cidade),
    estado: textoNaoVazio(lead.estado),
    cep: textoNaoVazio(lead.cep).replace(/\D/g, ''),
  };
}

/** Completa endereço via ViaCEP quando há CEP e faltam campos. */
export async function completarEnderecoViaCep(cep: string): Promise<{
  endereco?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
} | null> {
  const digits = String(cep || '').replace(/\D/g, '');
  if (digits.length !== 8) return null;
  try {
    const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    const data = await response.json();
    if (data?.erro) return null;
    return {
      endereco: textoNaoVazio(data.logradouro) || undefined,
      bairro: textoNaoVazio(data.bairro) || undefined,
      cidade: textoNaoVazio(data.localidade) || undefined,
      estado: textoNaoVazio(data.uf) || undefined,
    };
  } catch {
    return null;
  }
}

export function enderecoIncompleto(campos: {
  cep?: string;
  endereco?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
}): boolean {
  return !(
    textoNaoVazio(campos.cep) &&
    textoNaoVazio(campos.endereco) &&
    textoNaoVazio(campos.cidade) &&
    textoNaoVazio(campos.estado)
  );
}

type SupabaseQueryResult = Promise<{ data: Record<string, unknown>[] | null; error?: unknown }>;

type SupabaseLike = {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (col: string, val: string) => {
        neq: (col: string, val: string) => SupabaseQueryResult;
      };
    };
  };
};

/**
 * Se o lead atual não tem endereço completo, busca em leads irmãos
 * (mesmo CNPJ para PJ / mesmo CPF para PF) e completa os campos vazios.
 * Necessário no multi-produto: o usuário pode ter preenchido o endereço
 * em outra negociação do mesmo cliente.
 */
export async function enriquecerCamposComIrmaos(
  lead: Record<string, unknown> | null | undefined,
  supabaseClient: SupabaseLike
): Promise<CamposClienteContrato | null> {
  const base = camposClienteFromLead(lead);
  if (!base || !lead?.id) return base;

  const precisaEndereco = enderecoIncompleto(base);
  const precisaCpfRep = !textoNaoVazio(base.cpf_representante);
  if (!precisaEndereco && !precisaCpfRep) return base;

  const isPj = base.tipoCliente === 'PJ';
  const docField = isPj ? 'cnpj_normalizado' : 'cpf_normalizado';
  const docValue = textoNaoVazio(lead[docField]);
  if (!docValue) return base;

  try {
    const { data: irmaos } = await supabaseClient
      .from('leads')
      .select(
        'id, cep, endereco, numero, bairro, cidade, estado, cpf_representante, representante_legal, cnpj_normalizado, cpf_normalizado'
      )
      .eq(docField, docValue)
      .neq('id', String(lead.id));

    if (!irmaos?.length) return base;

    let merged = { ...base };
    for (const irmao of irmaos) {
      const outro = camposClienteFromLead({ ...lead, ...irmao });
      if (!outro) continue;
      merged = {
        ...merged,
        cep: preferirPreenchido(merged.cep, outro.cep),
        endereco: preferirPreenchido(merged.endereco, outro.endereco),
        numero: preferirPreenchido(merged.numero, outro.numero),
        bairro: preferirPreenchido(merged.bairro, outro.bairro),
        cidade: preferirPreenchido(merged.cidade, outro.cidade),
        estado: preferirPreenchido(merged.estado, outro.estado),
        cpf_representante: preferirPreenchido(merged.cpf_representante, outro.cpf_representante),
        representante_legal: preferirPreenchido(
          merged.representante_legal,
          outro.representante_legal
        ).toUpperCase(),
      };
      if (!enderecoIncompleto(merged) && textoNaoVazio(merged.cpf_representante)) break;
    }
    return merged;
  } catch (e) {
    console.error('Erro ao buscar endereço em leads irmãos:', e);
    return base;
  }
}
